import axios from 'axios';
import uuid from 'uuid';
import { callNotificationListeners } from './hooks/callbacks';
import { Toast } from './types';

type SocketOpts = {
  delay: number;
};
export default class Socket {
  debug: boolean;

  url: string;

  opts: SocketOpts;

  attempts: number;

  token?: string;

  store: any;

  C: any;

  ws?: WebSocket;

  experience: string;

  id: string;

  constructor({
    url,
    store,
    C,
    debug = false,
    opts = {
      delay: 5000,
    },
    experience = 'others',
  }: {
    url: string;
    store: any;
    token?: string;
    C: any;
    debug?: boolean;
    opts: SocketOpts;
    experience: string;
  }) {
    this.id = uuid.v4();
    this.url = url;
    this.store = store;
    this.C = C;
    this.opts = opts;
    this.attempts = 1;
    this.debug = debug;
    this.experience = experience;
  }

  generateInterval() {
    let maxInterval = (2 ** this.attempts - 1) * 1000;
    this.attempts += 1;

    if (maxInterval > 30 * 1000) {
      maxInterval = 30 * 1000;
    }

    return Math.random() * maxInterval;
  }

  downloadFile(url) {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.dispatchEvent(new MouseEvent('click'));
  }

  get isActive() {
    let active: string | null;
    try {
      active = window.localStorage.getItem(`notifications`);
    } catch (error) {
      active = null;
    }

    if (!active || active === this.id) {
      return true;
    }

    return false;
  }

  setActive(active: boolean) {
    if (this.ws && this.ws.readyState === 1) {
      if (active) {
        try {
          window.localStorage.setItem('notifications', this.id);
        } catch (error) {
          // do nothing
        }
      }
      this.ws.send(
        JSON.stringify({
          message: 'state',
          data: {
            id: this.id,
            active,
          },
        })
      );
    }
  }

  processMessage(msg: Toast) {
    const payload = {
      type: this.C.NOTIFY_WEBSOCKET_MESSAGE_SUCCESS,
      notificationPayload: msg.message,
      notificationDelay: this.opts.delay || this.C.NOTIFICATION_DELAY,
      notificationType: msg.type || 'info',
      permalink: msg.permalink,
      data: {
        message: msg.message,
        notificationDelay: this.opts.delay || this.C.NOTIFICATION_DELAY,
        notificationType: msg.type || 'info',
        permalink: msg.permalink,
      },
    };

    callNotificationListeners({
      data: msg,
      notificationType: 'toast',
    });

    this.store.dispatch(payload);

    if (msg.metadata?.closeModal) {
      this.store.dispatch({ type: this.C.CLOSE_MODAL });
    }

    if (msg.link && this.isActive) {
      this.downloadFile(msg.link);
    }
  }

  processNotification(data) {
    const payload = {
      type: this.C.RECEIVE_NOTIFICATIONS_SUCCESS,
      data,
    };

    // this returns a list of all notifications, we just want
    // the most recent one
    callNotificationListeners({
      data: data?.data?.[0],
      notificationType: 'notification',
    });
    this.store.dispatch(payload);
  }

  setupSocket(ws: WebSocket) {
    this.ws = ws;

    this.ws.onmessage = msg => {
      // eslint-disable-line
      const data = JSON.parse(msg.data);
      if (data.message === 'PONG') return null;
      if ('notifications' in data) {
        return this.processNotification(data.notifications);
      }
      return this.processMessage(data);
    };

    this.ws.onopen = () => {
      // when we open the connection attempt
      // to determine if we are the active tab
      this.setActive(!document.hidden);

      // eslint-disable-line
      this.attempts = 1;
      const ping = () => {
        if (ws.readyState === 1) {
          try {
            ws.send(
              JSON.stringify({
                message: 'PING',
                timestamp: Date.now().toString(),
                id: this.id,
              })
            );
            setTimeout(ping, 60 * 60 * 5);
          } catch (e) {
            const time = this.generateInterval();
            setTimeout(this.connect.bind(this), time); // eslint-disable-line
          }
        }
      };
      ping();
    };

    this.ws.onclose = () => {
      // eslint-disable-line
      const time = this.generateInterval();
      setTimeout(this.connect.bind(this), time); // eslint-disable-line
    };
  }

  async connect() {
    document.addEventListener('visibilitychange', () => {
      this.setActive(!document.hidden);
    });

    const auth = await axios.get(`/v1/token`).catch(() => null);

    if (auth) {
      const token = await axios.post(
        `${process.env.NOTIFICATIONS_URL}/v1/token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${auth.data}`,
          },
        }
      );
      this.setupSocket(
        new WebSocket(
          `${this.url}?token=${token.data.token}&experience=${this.experience}&id=${this.id}`
        )
      );
    }
  }
}
